import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ConjunctionMode } from "../../segments/segment-types";
import {
  NumericCompareOperator,
  tokenHierarchyPrerequisiteCounts,
  WhereConditionDataType,
  whereConditionDataTypeOperators,
} from "../../segments/segment-where-condition";
import {
  CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE,
  CustomerAttributeDataStructure,
  type SegmentDataStructureField,
  type SegmentDataStructureType,
  segmentDataStructureTypeToDataStructure,
} from "./segment-data-structures";
import { fullDateTimeframeConditionTypes } from "./segment-timeframe";
import {
  FetchStrategy,
  getTokenFetchStrategy,
} from "./segment-token-fetch-strategy";
import {
  CustomerActivityType,
  CustomerCharacteristicType,
  CustomerDataSource,
  SegmentConditionCategory,
  SegmentConditionType,
} from "./segment-types";
import { segmentSchema } from "./segment-zod-schema";

export const segmentCatalogFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  category: createEnum(SegmentConditionCategory, {
    enumName: "SegmentConditionCategory",
  }),
  dataType: createEnum(WhereConditionDataType, {
    enumName: "WhereConditionDataType",
  }),
  operators: z.array(z.string()),
  needsTokenOptions: z.boolean(),
  tokenPrerequisiteCount: z.number().nullable(),
  unit: z.enum(["percent", "currency"]).nullable(),
});

export const segmentCatalogActivitySchema = z.object({
  id: createEnum(CustomerActivityType, { enumName: "CustomerActivityType" }),
  name: z.string(),
  description: z.string(),
  category: createEnum(SegmentConditionCategory, {
    enumName: "SegmentConditionCategory",
  }),
  source: createEnum(CustomerDataSource, { enumName: "CustomerDataSource" }),
  fields: z.array(segmentCatalogFieldSchema),
  countOperators: z.array(z.string()),
  timeframeTypes: z.array(z.string()),
});

export const segmentCatalogCustomEventSchema = z.object({
  description: z.string(),
  eventNameNeedsTokenOptions: z.boolean(),
  countOperators: z.array(z.string()),
  timeframeTypes: z.array(z.string()),
});

export const segmentOperatorSetSchema = z.object({
  dataType: createEnum(WhereConditionDataType, {
    enumName: "WhereConditionDataType",
  }),
  operators: z.array(z.string()),
});

export const segmentConditionCatalogSchema = z.object({
  conjunctions: z.array(
    createEnum(ConjunctionMode, { enumName: "ConjunctionMode" }),
  ),
  conditionKinds: z.array(
    createEnum(SegmentConditionType, { enumName: "SegmentConditionType" }),
  ),
  operatorSets: z.array(segmentOperatorSetSchema),
  customerAttributes: z.array(segmentCatalogFieldSchema),
  customerActivities: z.array(segmentCatalogActivitySchema),
  customEvent: segmentCatalogCustomEventSchema,
  conditionsJsonSchema: z.unknown(),
});

export type SegmentCatalogField = z.infer<typeof segmentCatalogFieldSchema>;
export type SegmentCatalogActivity = z.infer<
  typeof segmentCatalogActivitySchema
>;
export type SegmentCatalogCustomEvent = z.infer<
  typeof segmentCatalogCustomEventSchema
>;
export type SegmentOperatorSet = z.infer<typeof segmentOperatorSetSchema>;
export type SegmentConditionCatalog = z.infer<
  typeof segmentConditionCatalogSchema
>;

export function operatorsForDataType(
  dataType: WhereConditionDataType,
): string[] {
  return [...whereConditionDataTypeOperators[dataType]];
}

function needsTokenOptions(
  dataStructure: SegmentDataStructureType,
  field: SegmentDataStructureField<string>,
): boolean {
  return (
    getTokenFetchStrategy({ dataStructure, dataStructureField: field.type }) !==
    FetchStrategy.NONE
  );
}

function catalogField(
  dataStructure: SegmentDataStructureType,
  field: SegmentDataStructureField<string>,
): SegmentCatalogField {
  const prerequisiteCount =
    field.dataType === WhereConditionDataType.CUSTOM_FIELD
      ? 0
      : field.dataType === WhereConditionDataType.TOKEN_HIERARCHY ||
          field.dataType === WhereConditionDataType.CITY_PROXIMITY
        ? (tokenHierarchyPrerequisiteCounts[
            field.type as CustomerCharacteristicType
          ] ?? null)
        : null;
  return {
    id: field.type,
    name: field.name,
    description: field.description,
    category: field.category,
    dataType: field.dataType,
    operators: operatorsForDataType(field.dataType),
    needsTokenOptions: needsTokenOptions(dataStructure, field),
    tokenPrerequisiteCount: prerequisiteCount ?? null,
    unit: field.unit ?? null,
  };
}

function humanizeActivityId(id: string, source: CustomerDataSource): string {
  const platforms: [string, string][] = [
    ["-commentsold", " (CommentSold)"],
    ["-bigcommerce", " (BigCommerce)"],
  ];
  let core = id;
  let suffix = "";
  for (const [ending, label] of platforms) {
    if (core.endsWith(ending)) {
      core = core.slice(0, -ending.length);
      suffix = label;
      break;
    }
  }
  if (!suffix && source === CustomerDataSource.SHOPIFY) {
    suffix = " (Shopify)";
  }
  const titled = core
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return `${titled}${suffix}`;
}

function platformFromActivityEnumKey(
  type: CustomerActivityType,
): CustomerDataSource | undefined {
  const key = (
    Object.entries(CustomerActivityType) as [string, CustomerActivityType][]
  ).find(([, value]) => value === type)?.[0];
  if (key?.endsWith("_COMMENTSOLD")) {
    return CustomerDataSource.COMMENTSOLD;
  }
  if (key?.endsWith("_BIGCOMMERCE")) {
    return CustomerDataSource.BIGCOMMERCE;
  }
  if (key?.endsWith("_SHOPIFY")) {
    return CustomerDataSource.SHOPIFY;
  }
  return undefined;
}

function sourceForActivity(type: CustomerActivityType): CustomerDataSource {
  const fieldSources = [
    ...new Set(
      Object.values(segmentDataStructureTypeToDataStructure[type].fields).map(
        (field) => field.source,
      ),
    ),
  ];
  if (fieldSources.length === 1) {
    return fieldSources[0]!;
  }
  if (fieldSources.length > 1) {
    return CustomerDataSource.REDO;
  }
  const fromEnumKey = platformFromActivityEnumKey(type);
  if (fromEnumKey) {
    return fromEnumKey;
  }
  if (type.includes("commentsold")) {
    return CustomerDataSource.COMMENTSOLD;
  }
  if (type.includes("bigcommerce")) {
    return CustomerDataSource.BIGCOMMERCE;
  }
  if (type.includes("shopify")) {
    return CustomerDataSource.SHOPIFY;
  }
  return CustomerDataSource.REDO;
}

function categoryForActivity(
  fields: SegmentCatalogField[],
  type: CustomerActivityType,
): SegmentConditionCategory {
  if (fields[0]) {
    return fields[0].category;
  }
  const marketing = new Set<CustomerActivityType>([
    CustomerActivityType.RECEIVED_EMAIL,
    CustomerActivityType.OPENED_EMAIL,
    CustomerActivityType.CLICKED_EMAIL,
    CustomerActivityType.RECEIVED_TEXT,
    CustomerActivityType.CLICKED_TEXT,
    CustomerActivityType.BOUNCED_EMAIL,
    CustomerActivityType.REPLIED_SMS,
    CustomerActivityType.MARKETING_SUBSCRIBED,
    CustomerActivityType.MARKETING_CONFIRMED,
  ]);
  return marketing.has(type)
    ? SegmentConditionCategory.MARKETING
    : SegmentConditionCategory.SHOPPING;
}

const COUNT_OPERATORS = Object.values(NumericCompareOperator);
const ACTIVITY_TIMEFRAME_TYPES = [...fullDateTimeframeConditionTypes];

export function buildSegmentConditionCatalog(): SegmentConditionCatalog {
  const customerAttributes = Object.values(
    CustomerAttributeDataStructure.fields,
  ).map((field) => catalogField(CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE, field));

  const customerActivities = Object.values(CustomerActivityType).map((type) => {
    const fields = Object.values(
      segmentDataStructureTypeToDataStructure[type].fields,
    ).map((field) => catalogField(type, field));
    const source = sourceForActivity(type);
    return {
      id: type,
      name: humanizeActivityId(type, source),
      description: `Customers who ${humanizeActivityId(type, source).toLowerCase()}.`,
      category: categoryForActivity(fields, type),
      source,
      fields,
      countOperators: COUNT_OPERATORS,
      timeframeTypes: ACTIVITY_TIMEFRAME_TYPES,
    };
  });

  const operatorSets = Object.values(WhereConditionDataType).map(
    (dataType) => ({ dataType, operators: operatorsForDataType(dataType) }),
  );

  return {
    conjunctions: [ConjunctionMode.AND, ConjunctionMode.OR],
    conditionKinds: [
      SegmentConditionType.CUSTOMER_ATTRIBUTE,
      SegmentConditionType.CUSTOMER_ACTIVITY,
      SegmentConditionType.CUSTOM_EVENT,
    ],
    operatorSets,
    customerAttributes,
    customerActivities,
    customEvent: {
      description:
        "Filter by a custom event sent through the Redo Custom Events API. Use segmentTokenFieldOptions to list event names for this store.",
      eventNameNeedsTokenOptions: true,
      countOperators: COUNT_OPERATORS,
      timeframeTypes: ACTIVITY_TIMEFRAME_TYPES,
    },
    conditionsJsonSchema: z.toJSONSchema(segmentSchema, {
      io: "input",
      unrepresentable: "any",
    }),
  };
}
